import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import toast from "react-hot-toast";
import { setAuthorization } from "@/app_redux/reducers/auth_reducer";
import { store } from "@/app_redux/stores/root_store";
import { AUTH_APIS } from "./api_config";
import { logLogoutDiagnostic } from "./logoutDiagnostics";

// Enable cookie sending selectively for the login and refresh endpoints.

// --- Refresh token queue mechanism ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

// URLs that should NOT trigger a refresh on 401 (login/refresh endpoints themselves)
const REFRESH_BYPASS_URLS = [AUTH_APIS.TOKEN_REFRESH.baseURL, AUTH_APIS.IAM_LOGIN.baseURL];

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status;

    if (status === 413) {
      toast.error("File size is too large. Please compress and try again");
      return Promise.reject(error);
    }

    const isUnauthorized = status === 401 || status === 403;

    // Only handle 401/403 auth errors, skip if already retried or if it's a login/refresh call
    if (
      !isUnauthorized ||
      originalRequest._retry ||
      REFRESH_BYPASS_URLS.some((url) => originalRequest.url?.includes(url))
    ) {
      return Promise.reject(error);
    }

    // If another request is already refreshing, queue this one
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = newToken;
        return axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(AUTH_APIS.TOKEN_REFRESH.baseURL, {}, { withCredentials: true });

      const newAccessToken = refreshResponse.data?.data?.access_token;
      if (!newAccessToken) {
        throw new Error("No access token in refresh response");
      }

      const bearerToken = `Bearer ${newAccessToken}`;

      localStorage.setItem("access_token", bearerToken);
      store.dispatch(setAuthorization({ accessToken: bearerToken, isValid: true }));

      // Retry all queued requests
      processQueue(null, bearerToken);

      // Retry the original request
      originalRequest.headers.Authorization = bearerToken;
      return axios(originalRequest);
    } catch (refreshError) {
      // Refresh failed — token is truly expired, force logout
      processQueue(refreshError, null);

      // Diagnostic: this is the mid-EMR logout culprit. Records WHICH api call
      // 401'd and WHY the refresh failed, before we write VMS_LOGOUT_EVENT (which
      // the App.tsx poller turns into an actual clear-storage + redirect).
      const refreshAxiosError = refreshError as AxiosError;
      logLogoutDiagnostic("AXIOS_REFRESH_FAILED_REDIRECT_TO_LOGIN", {
        failed_url: originalRequest?.url,
        failed_status: status,
        refresh_status: refreshAxiosError?.response?.status,
        access_token: localStorage.getItem("access_token"),
        refresh_error: refreshAxiosError?.message,
        clinic_id: localStorage.getItem("clinic"),
        clinic_name: localStorage.getItem("clinic_name"),
        vms_user_id: localStorage.getItem("vms_user_id"),
        vms_user_email: localStorage.getItem("vms_user_email"),
        vms_user_last_activity: localStorage.getItem("vms_user_last_activity"),
      });

      // Clear auth state and redirect to login
      localStorage.removeItem("access_token");
      localStorage.setItem("VMS_LOGOUT_EVENT", Date.now().toString());

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
